<template>
  <v-container fluid>
    <v-row class="mb-1">
      <v-col>
        <h2>
          <v-icon class="mr-2"> mdi-list-status </v-icon>
          マニフェスト管理
        </h2>
      </v-col>
    </v-row>

    <search-block
      ref="searchBlock"
      :conditions.sync="conditions"
      :condition-patterns.sync="conditionPatterns"
      :selectable-condition-pattern.sync="selectableConditionPattern"
      :is-jw-mode="isJwMode"
      :items="items"
      :disposal-methods="disposalMethods"
      :vote-staffs="voteStaffs"
      :custom-label="customLabel"
      :stop-search="stopSearch"
      @search="searchCallback"
      @clear="clearCallback"
      @addConditionPattern="addConditionPatternCallback"
      @removeConditionPattern="removeConditionPatternCallback"
      @userTabChange="handleUserTabChange"
      @update:selectableConditionPattern="selectableConditionPatternChangeCallback"
    />

    <!-- ボタン -->
    <v-row align="center" class="mt-1">
      <v-col cols="12" md="12">
        <v-card-actions
          style="overflow-x: auto; flex-wrap: nowrap; padding: 8px"
        >
          <v-btn-toggle
            v-if="isDisposalMode()"
            v-model="conditions.type_of_manifest"
            class="toggleButton"
            color="info accent-3"
            tile
            light
            mandatory
            rounded
          >
            <v-btn
              value="first_manifest"
              @click="typeOfManifestChangeEvent('first_manifest')"
            >
              １次マニフェスト
            </v-btn>
            <v-btn
              value="second_manifest"
              @click="typeOfManifestChangeEvent('second_manifest')"
            >
              ２次マニフェスト
            </v-btn>
          </v-btn-toggle>
          <!-- 1週間分の日付ボタン -->
          <div class="d-flex align-center week-selector">
            <!-- 前の週へ移動ボタン -->
            <v-btn icon @click="moveToPreviousWeek">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>

            <!-- 1週間分の日付ボタン -->
            <div>
              <v-tooltip
                v-for="dateInfo in weekDates"
                :key="dateInfo.date"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    :class="[
                      'date-button',
                      { 'today-border': dateInfo.isToday },
                      {
                        'blue--text':
                          dateInfo.isSaturday &&
                          conditions.target_date !== dateInfo.date,
                      },
                      {
                        'red--text':
                          dateInfo.isSunday &&
                          conditions.target_date !== dateInfo.date,
                      },
                      {
                        'primary white--text':
                          conditions.target_date === dateInfo.date,
                      },
                      { white: conditions.target_date !== dateInfo.date },
                    ]"
                    @click="dateButtonClick(dateInfo.date)"
                  >
                    <div style="text-align: center; line-height: 1.2">
                      <div style="font-size: 0.8rem">
                        {{ dateInfo.dayOfWeek }}
                      </div>
                      <div style="font-size: 0.7rem; font-weight: bold">
                        {{ dateInfo.displayDate }}
                      </div>
                    </div>
                  </v-btn>
                </template>
                <span>引渡し日: {{ dateInfo.date }}</span>
              </v-tooltip>
            </div>

            <!-- 次の週へ移動ボタン -->
            <v-btn icon @click="moveToNextWeek">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          <v-btn
            v-if="conditions.recovery_order_id && conditions.recovery_order_type"
            class="btn"
            @click="backToOrderButtonClickEvent"
          >
            受注詳細に戻る
          </v-btn>

          <v-spacer />

          <template v-if="!isEcContractSinglePlan">
            <output-reservation-dialog
              section="management_manifest"
              :enable-blink="blinkingButton"
              @stopBlinkCallback="blinkingButton = false"
            />
          </template>

          <v-btn
            v-if="isEcContractSinglePlan"
            :class="blinkingButton ? 'blinkButton' : ''"
            @click="ecContractSinglePlanButtonClickEvent"
          >
            <v-icon>mdi-download</v-icon>&nbsp;出力履歴
          </v-btn>
          &emsp;
          <v-btn
            v-if="isEcContractSinglePlan"
            color="info"
            @click="ecContractSinglePlanButtonClickEvent"
          >
            <v-icon>mdi-file-download-outline</v-icon>&nbsp;CSV出力
          </v-btn>

          <template v-if="!isEcContractSinglePlan">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="info" dark v-bind="attrs" v-on="on">
                  <v-icon>mdi-file-download-outline</v-icon>&nbsp;CSV出力
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="exportButtonClickEvent">
                  <v-list-item-title>全件出力</v-list-item-title>
                </v-list-item>
                <v-list-item @click="exportDirectButtonClickEvent">
                  <v-list-item-title>直行用出力</v-list-item-title>
                </v-list-item>
                <v-list-item @click="exportTransshipmentButtonClickEvent">
                  <v-list-item-title>積替保管用出力</v-list-item-title>
                </v-list-item>
                <v-list-item @click="exportImportSampleButtonClickEvent">
                  <v-list-item-title>取込用ひな形出力</v-list-item-title>
                </v-list-item>
              </v-list> </v-menu
            >&nbsp;
          </template>

          <v-btn
            v-if="!isDemoEnv"
            color="info"
            @click="importManifestButtonClickEvent"
          >
            <v-icon>mdi-file-upload-outline</v-icon>&nbsp;CSV取込
          </v-btn>
          &emsp;
          <v-btn
            v-if="isEcContractSinglePlan"
            color="primary"
            class="text-h6"
            x-large
            v-bind="attrs"
            v-on="on"
            @click="ecContractSinglePlanButtonClickEvent"
          >
            新規作成&nbsp;
            <v-icon large> mdi-plus </v-icon>
          </v-btn>

          <template v-if="!isEcContractSinglePlan">
            <v-menu
              :disabled="
                conditions.type_of_manifest === 'second_manifest' &&
                (!isDisposalMode() || isDemo)
              "
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :disabled="
                    conditions.type_of_manifest === 'second_manifest' &&
                    (!isDisposalMode() || isDemo)
                  "
                  color="primary"
                  class="text-h6"
                  x-large
                  v-bind="attrs"
                  v-on="on"
                >
                  新規作成&nbsp;
                  <v-icon large> mdi-plus </v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item link @click="addButtonClickEvent">
                  <v-list-item-title> 新規作成 </v-list-item-title>
                </v-list-item>
                <v-list-item link @click="showDuplicateDialogButtonClickEvent">
                  <v-list-item-title> 複写して作成 </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-card-actions>
      </v-col>
    </v-row>
    <!-- /ボタン -->

    <v-row class="white">
      <v-col cols="12" md="6" style="vertical-align: middle">
        <div>
          <small
            >排出者承認　<span style="color: #f0c000">▲</span>：未承認　<span
              style="color: #f57c00"
              >●</span
            >：承認済（本登録前）　<span style="color: #0074ba">●</span
            >：承認済（本登録済）　<span style="color: red">×</span
            >：差戻</small
          >
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <custom-pagination
          :page-number="page"
          :per-page="perPage"
          :pagination="pagination"
          :total-visible="totalVisible"
          @pageChanged="changePage"
          @perPageChanged="changePerPage"
        />
      </v-col>
    </v-row>

    <!-- 一覧 -->
    <v-sheet class="mt-5 mb-5" rounded>
      <v-data-table
        v-model="rowSelected"
        :headers="tableHeaders"
        :items="manifests"
        :items-per-page="-1"
        hide-default-footer
        disable-sort
        show-select
        @click:row="toggleRowSelection"
      >
        <!-- ステータス/マニフェスト番号 -->
        <template v-slot:[`item.condition_status`]="{ item }">
          <div>
            <v-chip
              v-if="item.condition_status"
              :title="item.condition_status"
              :color="item.condition_status | manifest_regist_status_color"
              drak
              label
              x-small
            >
              {{ item.condition_status | manifest_regist_status_label }}
            </v-chip>
            &nbsp;
          </div>
          <div>{{ item.manifest_no }}&nbsp;</div>
          <div>
            <small class="text--disabled"
            >{{
                item.manifest_type | manifest_type_to_label
              }}&nbsp;</small
            >
          </div>
        </template>
        <!-- DXEマニ番号/受注番号 -->
        <template v-slot:[`item.order_no`]="{ item }">
          <div>{{ item.id }}&nbsp;</div>
          <div v-if="item.order_no">
            <v-btn
              text
              color="primary"
              x-small
              class="pa-0"
              style="font-size: 0.75rem; min-width: auto"
              @click="orderNoClickEvent(item)"
            >
              {{ item.order_no }}
            </v-btn>
          </div>
          <div v-else>{{ "-" }}</div>
        </template>
        <!-- 状態/引渡し日/運搬担当者 -->
        <template v-slot:[`item.classification_type`]="{ item }">
          <div>
            <v-chip
              v-if="item.waiting_data_coordination"
              color="black"
              dark
              label
              x-small
            >取込中</v-chip
            >
            <v-chip
              v-if="
                    !item.waiting_data_coordination && item.classification_type
                  "
              label
              x-small
              :color="
                    item.classification_type | manifest_condition_status_color
                  "
            >
              {{
                item.classification_type | manifest_condition_status_label
              }}
            </v-chip>
          </div>
          <div>{{ item.collected_date | date_format }}&nbsp;</div>
          <div>
            <small class="text--disabled">{{
                item.transport_user_name
              }}</small>
          </div>
        </template>
        <!-- /状態/引渡し日/運搬担当者 -->
        <!-- 回収先/排出事業場  -->
        <template v-slot:[`item.emission`]="{ item }">
          <div>
            <div class="inline-flex-container">
              <div class="line-clamp one-clamp">
                {{ item.emission.name }}
              </div>
              <v-tooltip top :open-on-hover="false">
                <template #activator="{ on }">
                  <v-btn
                    icon
                    tile
                    small
                    retain-focus-on-click
                    class="map-icon"
                    @click="on.click"
                    @blur="on.blur"
                  >
                    <v-icon small>mdi-map-marker-outline</v-icon>
                  </v-btn>
                </template>
                <div>
                  <strong>{{ item.emission.name }}</strong>
                  <br />
                  <small>{{ item.emission.address }}</small>
                </div>
              </v-tooltip>
            </div>
          </div>
          <div>
            <div class="inline-flex-container">
              <div class="line-clamp one-clamp">
                {{ item.emission_location.name }}
              </div>
              <v-tooltip top :open-on-hover="false">
                <template #activator="{ on }">
                  <v-btn
                    icon
                    tile
                    small
                    retain-focus-on-click
                    class="map-icon"
                    @click="on.click"
                    @blur="on.blur"
                  >
                    <v-icon small>mdi-map-marker-outline</v-icon>
                  </v-btn>
                </template>
                <div>
                  <strong>{{ item.emission_location.name }}</strong>
                  <br />
                  <small>{{ item.emission_location.address }}</small>
                </div>
              </v-tooltip>
            </div>
          </div>
        </template>
        <!-- /回収先/排出事業場  -->

        <!-- 承認状況 -->
        <template v-slot:[`item.approval_status`]="{ item }">
          <div>
                <span v-if="item.approval_status === 'before_reserve'">
                  ー
                </span>
            <span
              v-if="
                    item.approval_status === 'send_target' ||
                    item.approval_status === 'await_approval' ||
                    item.approval_status === 'reapplication'
                  "
              style="color: #f0c000"
            >
                  ▲
                </span>
            <span
              v-if="
                    item.approval_status === 'approved' &&
                    !item.manifest_registered
                  "
              style="color: #f57c00"
            >
                  ●
                </span>
            <span
              v-if="
                    item.approval_status === 'approved' &&
                    item.manifest_registered
                  "
              style="color: #0074ba"
            >
                  ●
                </span>
            <span
              v-if="item.approval_status === 'remand'"
              style="color: red"
            >
                  ×
                </span>
          </div>
        </template>
        <!-- /承認状況 -->

        <!-- 収集運搬業者（区間１）/処分事業場 -->
        <template v-slot:[`item.disposer_location`]="{ item }">
          <div>
            <div class="inline-flex-container">
              <div style="display: flex; align-items: center; gap: 4px;">
                <v-img
                  v-if="item.use_station"
                  max-height="18"
                  max-width="18"
                  :src="require(`@/assets/images/DXE_mark.svg`)"
                />
                <span :title="item.name" class="line-clamp one-clamp">{{ item.first_transport_name }} </span>
              </div>
              <v-tooltip top :open-on-hover="false">
                <template #activator="{ on }">
                  <v-btn
                    icon
                    tile
                    small
                    retain-focus-on-click
                    class="map-icon"
                    @click="on.click"
                    @blur="on.blur"
                  >
                    <v-icon small>mdi-map-marker-outline</v-icon>
                  </v-btn>
                </template>
                <div>
                  <strong>{{ item.first_transport_name }}</strong>
                  <br />
                  <small>{{ item.first_transport_address }}</small>
                </div>
              </v-tooltip>
            </div>
          </div>
          <div>
            <div class="inline-flex-container">
              <div style="display: flex; align-items: center; gap: 4px;">
                <v-img
                  v-if="item.disposer_location.use_station"
                  max-height="18"
                  max-width="18"
                  :src="require(`@/assets/images/DXE_mark.svg`)"
                />
                <span :title="item.name" class="line-clamp one-clamp">{{ item.disposer_location.name }} </span>
              </div>
              <v-tooltip top :open-on-hover="false">
                <template #activator="{ on }">
                  <v-btn
                    icon
                    tile
                    small
                    retain-focus-on-click
                    class="map-icon"
                    @click="on.click"
                    @blur="on.blur"
                  >
                    <v-icon small>mdi-map-marker-outline</v-icon>
                  </v-btn>
                </template>
                <div>
                  <strong>{{ item.disposer_location.name }}</strong>
                  <br />
                  <small>{{ item.disposer_location.address }}</small>
                </div>
              </v-tooltip>
            </div>
          </div>
        </template>
        <!-- /収集運搬業者（区間１）/処分事業場 -->
        <!-- 品目/処分方法/数量 -->
        <template v-slot:[`item.item_name`]="{ item }">
          <div>
            <v-chip
              :title="item.item_name"
              color="#F0E6D0"
              drak
              label
              x-small
              class="chip-ellipsis"
            >
              {{ item.item_name }}
            </v-chip>
          </div>
          <div>
            <small>{{ item.disposal_method_name }}&nbsp;</small>
          </div>
          <div>
            <small class="text--disabled"
            >{{ item.report_transport_quantity }}&nbsp;{{
                item.report_transport_quantity_unit_name
              }}</small
            >
          </div>
        </template>
        <!-- /品目/処分方法/数量 -->
        <!-- 登録日/本登録日 -->
        <template v-slot:[`item.created_unix_time`]="{ item }">
          <div>
                <span v-if="item.created_unix_time">
                  {{ item.created_unix_time | unix_date_format }}&nbsp;
                </span>
            <span v-else> -&nbsp; </span>
          </div>
          <div>
                <span v-if="item.transport_end_report_processed_time">
                  {{
                    item.transport_end_report_processed_time | unix_date_format
                  }}&nbsp;
                </span>
            <span v-else> -&nbsp; </span>
          </div>
        </template>
        <!-- /登録完了日/運搬報告登録日 -->
        <!-- 運搬報告（最終区間） -->
        <template v-slot:[`item.transport_end_report_date`]="{ item }">
          <div>
                <span v-if="item.transport_end_report_date">
                  {{ item.transport_end_report_date | date_format }}
                </span>
            <span v-else>
                  {{ "-" }}
                </span>
          </div>
          <div>
                <span>
                  {{ item.transport_end_report_notice || "-" }}
                </span>
          </div>
        </template>
        <!-- /運搬報告（最終区間） -->
        <!-- 中間処分/最終処分 -->
        <template v-slot:[`item.disposal_end_middle_time`]="{ item }">
          <div>
                <span>
                  {{ item.disposal_end_middle_time || "-" }}
                </span>
          </div>
          <div class="text-center">
                <span>
                  {{ item.final_disposal_end_time || "-" }}
                </span>
            <span
              v-if="item.unreported_final_in_dxe"
              class="circle_chip red ml-1"
            >
                  未
                </span>
          </div>
        </template>
        <!-- /中間処分/最終処分 -->
        <!-- 編集 -->
        <template v-slot:[`item.editable`]="{ item }">
          <v-menu>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-if="
                      ['paper_form', 'other_form'].includes(
                        item.manifest_type
                      ) &&
                      ['editing'].includes(item.modify_type) &&
                      !item.order_no
                    "
                link
                @click="editButtonClickEvent(item)"
              >
                <v-list-item-title> 編集 </v-list-item-title>
              </v-list-item>
              <v-list-item
                v-else-if="
                      ['editing', 'correction'].includes(item.modify_type)
                    "
                link
                @click="editButtonClickEvent(item)"
              >
                <v-list-item-title> 修正・取消 </v-list-item-title>
              </v-list-item>
              <v-list-item link @click="showButtonClickEvent(item)">
                <v-list-item-title> 詳細を参照 </v-list-item-title>
              </v-list-item>
              <!--
                自社起票 かつ
                他社区間代行起票ON または 他社運搬でない場合に複写可能
              -->
              <v-list-item
                v-if="canShowDuplicateManu(item)"
                link
                @click="duplicateButtonClickEvent(item)"
              >
                <v-list-item-title> 複写 </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        <!-- /編集 -->
      </v-data-table>
    </v-sheet>
    <!-- /一覧 -->

    <!-- ページャ -->
    <v-row class="mb-15">
      <v-col>
        <custom-pagination
          :page-number="page"
          :per-page="perPage"
          :pagination="pagination"
          :total-visible="totalVisible"
          @pageChanged="changePage"
          @perPageChanged="changePerPage"
        />
      </v-col>
    </v-row>
    <!-- /ページャ -->

    <v-footer
      fixed
      class="mb-13"
      color="white"
      width="100%"
      style="overflow-x: auto"
    >
      <v-card flat width="100%">
        <v-card-actions>
          <v-spacer />
          <!-- 受渡確認票出力 -->
          <v-menu :disabled="enableCallButton">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                :disabled="enableCallButton || isEcContractSinglePlan"
                class="mr-3"
                color="info"
                v-bind="attrs"
                v-on="on"
              >
                受渡確認票出力
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="outputManifestButtonClickEvent">
                <v-list-item-title>自社区間</v-list-item-title>
              </v-list-item>
              <v-list-item @click="outputAllManifestButtonClickEvent">
                <v-list-item-title>全区間</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <!-- /受渡確認票出力 -->
          <!-- 一覧表出力 -->
          <v-btn
            :disabled="enableCallButton || isEcContractSinglePlan"
            class="mr-3"
            color="info"
            @click="outputAllListButtonClickEvent"
          >
            一覧表出力
          </v-btn>
          <!-- /一覧表出力 -->

          <!-- 本登録 -->
          <v-btn
            color="primary"
            class="mr-3"
            :disabled="enableCallButton || isEcContractSinglePlan"
            @click="registerManifestButtonClickEvent"
          >
            本登録
          </v-btn>
          <!-- /本登録 -->
          <!-- 紙マニ返却日 -->
          <v-menu :disabled="enableCallButton || isEcContractSinglePlan">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                :disabled="enableCallButton || isEcContractSinglePlan"
                class="mr-3"
                color="primary"
                v-bind="attrs"
                v-on="on"
              >
                紙マニ返却日
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item link @click="paperManifestDialogShowClickEvent">
                <v-list-item-title> 個別登録 </v-list-item-title>
              </v-list-item>
              <v-list-item link @click="bulkPaperManifestDialogShowClickEvent">
                <v-list-item-title> 一括登録 </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <!-- /紙マニ返却日 -->
          <!-- 運搬終了報告 -->
          <v-menu :disabled="enableCallButton || isEcContractSinglePlan">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                :disabled="enableCallButton || isEcContractSinglePlan"
                class="mr-3"
                color="primary"
                v-bind="attrs"
                v-on="on"
              >
                運搬終了報告
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item link @click="completionReportDialogClickEvent">
                <v-list-item-title> 個別登録 </v-list-item-title>
              </v-list-item>
              <v-list-item link @click="bulkCompletionReportDialogClickEvent">
                <v-list-item-title> 一括登録 </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <!-- /運搬終了報告 -->
          <!-- 処分終了報告 -->
          <v-menu :disabled="enableCallButton || isEcContractSinglePlan">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                :disabled="enableCallButton || isEcContractSinglePlan"
                class="mr-3"
                color="primary"
                v-bind="attrs"
                v-on="on"
              >
                処分終了報告
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item v-if="isDisposalMode()" link @click="disposalReportDialogClickEvent(true)">
                <v-list-item-title> 個別登録(再資源) </v-list-item-title>
              </v-list-item>
              <v-list-item v-if="isDisposalMode()" link @click="bulkDisposalReportDialogClickEvent(true)">
                <v-list-item-title> 一括登録(再資源) </v-list-item-title>
              </v-list-item>
              <v-divider v-if="canShowRecyclingDivider()"></v-divider>
              <v-list-item v-if="canShowDefaultDisposalActions()" link @click="disposalReportDialogClickEvent(false)">
                <v-list-item-title> 個別登録 </v-list-item-title>
              </v-list-item>
              <v-list-item  v-if="canShowDefaultDisposalActions()" link @click="bulkDisposalReportDialogClickEvent(false)">
                <v-list-item-title> 一括登録 </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <!-- /処分終了報告 -->
          <!-- 最終処分終了報告 -->
          <v-menu :disabled="enableCallButton || isEcContractSinglePlan">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                :disabled="enableCallButton || isEcContractSinglePlan"
                color="primary"
                v-bind="attrs"
                v-on="on"
              >
                最終処分終了報告
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-if="isDisposalMode()"
                link
                @click="finalDisposalReportDialogClickEvent('recycle')"
              >
                <v-list-item-title> 個別登録(再資源) </v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="isDisposalMode()"
                link
                @click="bulkFinalDisposalReportDialogClickEvent('recycle')"
              >
                <v-list-item-title> 一括登録(再資源) </v-list-item-title>
              </v-list-item>
              <v-divider v-if="canShowRecyclingDivider()"></v-divider>
              <v-list-item
                link
                @click="finalDisposalReportDialogClickEvent('default')"
              >
                <v-list-item-title>
                  {{ canShowDefaultDisposalActions() ? '個別登録' : '個別登録(紙型式)' }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item
                link
                @click="bulkFinalDisposalReportDialogClickEvent('default')"
              >
                <v-list-item-title>
                  {{ canShowDefaultDisposalActions() ? '一括登録' : '一括登録(紙型式)' }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <!-- /最終処分終了報告 -->
        </v-card-actions>
      </v-card>
    </v-footer>

    <!-- 編集 -->
    <edit-dialog
      :enable-dialog="enableEditDialog"
      :target-manifest-id="targetManifestId"
      :items="items"
      :quantity-units="quantityUnits"
      :valuables-quantity-units="valuablesQuantityUnits"
      :quantity-confirms="quantityConfirms"
      :packings="packings"
      :hazardous-substances="hazardousSubstances"
      :transportations="transportations"
      :disposal-methods="disposalMethods"
      :users="users"
      :drivers="drivers"
      :vehicles="vehicles"
      @closeEvent="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />
    <rest-edit-dialog
      :enable-dialog="enableRestEditDialog"
      :target-manifest-id="targetManifestId"
      :items="items"
      :quantity-units="quantityUnits"
      :valuables-quantity-units="valuablesQuantityUnits"
      :quantity-confirms="quantityConfirms"
      :packings="packings"
      :hazardous-substances="hazardousSubstances"
      :transportations="transportations"
      :disposal-methods="disposalMethods"
      :users="users"
      :drivers="drivers"
      :vehicles="vehicles"
      @closeEvent="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />
    <!-- /編集 -->
    <!-- 複写 -->
    <duplicate-dialog
      :enable-dialog="enableDuplicateDialog"
      :manifest-level="conditions.type_of_manifest"
      :vote-staffs="voteStaffs"
      @cancelCallback="dialogCloseCallback"
      @registerEvent="duplicateDialogRegisterCallback"
    />
    <!-- /複写 -->
    <!-- 運搬終了報告(個別) -->
    <completion-report-dialog
      :enable-dialog="enableCompletionDialog"
      :manifest-ids="targetManifestIds"
      :sort-type="conditions.sort_type"
      type="Managements"
      @cancelCallback="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />
    <!-- /運搬終了報告(個別) -->
    <!-- 運搬終了報告(一括) -->
    <bulk-completion-dialog
      :enable-dialog="enableBulkCompletionDialog"
      type="Managements"
      :manifest-ids="targetManifestIds"
      @cancelCallback="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />
    <!-- /運搬終了報告(一括) -->
    <!-- 処分終了報告(個別) -->
    <disposal-report-dialog
      :enable-dialog="enableDisposalReportDialog"
      :manifest-ids="targetManifestIds"
      :manifest-level="conditions.type_of_manifest"
      :sort-type="conditions.sort_type"
      type="Managements"
      :show-recycling-fields="showRecyclingFields"
      @cancelCallback="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />
    <!-- /処分終了報告(個別) -->
    <!-- 処分終了報告(一括) -->
    <bulk-disposal-report-dialog
      :enable-dialog="enableBulkDisposalReportDialog"
      :manifest-ids="targetManifestIds"
      :manifest-level="conditions.type_of_manifest"
      type="Managements"
      :show-recycling-fields="showRecyclingFields"
      @cancelCallback="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />
    <!-- /処分終了報告(一括) -->
    <!-- 最終処分終了報告(個別) -->
    <final-disposal-report-dialog
      :enable-dialog="enableFinalDisposalReportDialog"
      :manifest-ids="targetManifestIds"
      :sort-type="conditions.sort_type"
      type="Managements"
      @cancelCallback="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />

    <recycle-final-disposal-report-dialog
      :enable-dialog="enableRecycleFinalDisposalReportDialog"
      :manifest-ids="targetManifestIds"
      :sort-type="conditions.sort_type"
      type="Managements"
      :manifest-level="conditions.type_of_manifest"
      @cancelCallback="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />

    <!-- /最終処分終了報告(個別) -->
    <!-- 最終処分終了報告(一括) -->
    <bulk-final-disposal-report-dialog
      :enable-dialog="enableBulkFinalDisposalReportDialog"
      :manifest-ids="targetManifestIds"
      type="Managements"
      @cancelCallback="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />

    <bulk-recycle-final-disposal-report-dialog
      :enable-dialog="enableBulkRecycleFinalDisposalReportDialog"
      :manifest-ids="targetManifestIds"
      type="Managements"
      :manifest-level="conditions.type_of_manifest"
      @cancelCallback="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />

    <!-- /最終処分終了報告(一括) -->
    <!-- 紙マニ返却日(個別) -->
    <paper-register-dialog
      :enable-dialog="enablePaperRegisterDialog"
      :manifest-ids="targetManifestIds"
      :sort-type="conditions.sort_type"
      @cancelCallback="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />
    <!-- /紙マニ返却日(個別) -->

    <bulk-paper-register-dialog
      :enable-dialog="enableBulkPaperRegisterDialog"
      :manifest-ids="targetManifestIds"
      @cancelCallback="dialogCloseCallback"
      @registerEvent="editDialogRegisterCallback"
    />

    <bulk-regist-error-dialog
      :enable-dialog="enableBulkRegistErrorDialog"
      :error-messages="bulkRegistErrors"
      @cancelCallback="dialogCloseCallback"
    />

    <!-- アップグレード誘導モーダル -->
    <ec-contract-single-plan-guide-dialog
      :enable-dialog="enableEcContractSinglePlanGuideDialog"
      :from-manifest="true"
      @closeCallback="dialogCloseCallback"
    />
    <!-- /アップグレード誘導モーダル -->

    <v-dialog v-model="enableWarningDialog" persistent width="700">
      <v-card>
        <v-card-title class="text-h5 red darken-2 white--text">
          確認
        </v-card-title>
        <v-divider />
        <v-card-text>
          <h4 class="mt-4">処理できないマニフェストが含まれています。</h4>

          <ul v-if="invalid_messages">
            <li
              v-for="(invalid_message, index) in invalid_messages"
              :key="index"
              v-html="invalid_message"
            />
          </ul>
          <p v-if="warningDialogMessage">{{ warningDialogMessage }}</p>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn @click="enableWarningDialog = false"> 閉じる </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="enableWarningDialog2" persistent width="500">
      <v-card>
        <v-toolbar color="error" dark flat>
          <v-toolbar-title>確認</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text>
          <h4 class="mt-4">
            {{ warningDialogMessage2 }}
          </h4>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn @click="enableWarningDialog2 = false"> 閉じる </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm ref="confirm" />
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import OutputReservationDialog from "@/driver/components/OutputReservationDialog.vue";
import EditDialog from "./dialogs/_EditDialog";
import RestEditDialog from "./dialogs/_RestManifestEditDialog.vue";
import DuplicateDialog from "./dialogs/_DuplicateDialog";
import DisposalReportDialog from "../../dialogs/_DisposalReportDialog";
import CompletionReportDialog from "../../dialogs/_CompletionReportDialog";
import BulkCompletionDialog from "../../dialogs/_BulkCompletionReportDialog";
import BulkDisposalReportDialog from "../../dialogs/_BulkDisposalReportDialog";
import FinalDisposalReportDialog from "../../dialogs/_FinalDisposalReportDialog";
import BulkFinalDisposalReportDialog from "../../dialogs/_BulkFinalDisposalReportDialog";
import PaperRegisterDialog from "./dialogs/_PaperRegisterDialog.vue";
import BulkPaperRegisterDialog from "./dialogs/_BulkPaperRegisterDialog.vue";
import BulkRegistErrorDialog from "./dialogs/_BulkRegistErrorDialog.vue";
import Confirm from "../../../../components/Confirm";
import DownloadMixin from "../../../../mixins/download-mixin";
import CustomPagination from "@/driver/components/_CustomPagination.vue";
import EcContractSinglePlanGuideDialog from "../../dialogs/_EcContractSinglePlanGuideDialog";
import SearchBlock from "./_SearchBlock.vue";
import RecycleFinalDisposalReportDialog from "@/driver/pages/dialogs/_RecycleFinalDisposalReportDialog.vue";
import BulkRecycleFinalDisposalReportDialog from "@/driver/pages/dialogs/_BulkRecycleFinalDisposalReportDialog.vue";

/**
 * マニフェスト管理
 */
export default {
  name: "DriverManifestManagements",
  components: {
    RecycleFinalDisposalReportDialog,
    BulkRecycleFinalDisposalReportDialog,
    OutputReservationDialog,
    CustomPagination,
    Confirm,
    EditDialog,
    RestEditDialog,
    DuplicateDialog,
    BulkCompletionDialog,
    DisposalReportDialog,
    CompletionReportDialog,
    BulkDisposalReportDialog,
    FinalDisposalReportDialog,
    BulkFinalDisposalReportDialog,
    PaperRegisterDialog,
    BulkPaperRegisterDialog,
    BulkRegistErrorDialog,
    EcContractSinglePlanGuideDialog,
    SearchBlock,
  },
  mixins: [DownloadMixin],
  props: {},
  data() {
    return {
      conditionPatterns: [],
      enablePaperRegisterDialog: false,
      enableBulkPaperRegisterDialog: false,
      enableBulkRegistErrorDialog: false,
      enableEditDialog: false,
      enableRestEditDialog: false,
      enableDuplicateDialog: false,
      enableCompletionDialog: false,
      enableBulkCompletionDialog: false,
      enableDisposalReportDialog: false,
      enableBulkDisposalReportDialog: false,
      enableFinalDisposalReportDialog: false,
      enableRecycleFinalDisposalReportDialog: false,
      enableBulkFinalDisposalReportDialog: false,
      enableBulkRecycleFinalDisposalReportDialog: false,
      enableEcContractSinglePlanGuideDialog: false,
      showRecyclingFields: false,
      page: 1,
      perPage: 50,
      totalVisible: 7,
      searchType: "",
      weekStartDate: null, // 週の開始日
      selectableConditionPattern: 0,
      /* 検索フォーム */
      conditions: {
        /* 1次・2次マニフェスト区分 */
        type_of_manifest: "first_manifest",
        /* マニフェスト番号検索(range:範囲指定, specify: 番号指定) */
        specify_type: "range",
        /* マニフェスト番号 範囲開始 */
        start_no: null,
        /* マニフェスト番号 範囲終了 */
        end_no: null,
        /* マニフェスト番号 */
        manifest_numbers: null,

        /* マニフェスト区分 */
        manifest_type: null,
        /* 引渡日 開始日 */
        start_date: null,
        /* 引渡日 終了日 */
        end_date: null,
        /* JWNET取扱品目 */
        item_id: null,
        /* DXE起票担当者 */
        vote_staff: null,

        /* 回収先 排出事業者ID */
        supplier_id: null,
        /* 回収先 排出事業者種別 */
        supplier_type: "Emission",
        /* 回収先 排出事業場ID */
        supplier_location_id: null,
        /* 回収先 排出事業場コード */
        supplier_location_code: null,

        /* 回収先 収運・処分事業者ID */
        supplier_trader_id: null,
        /* 回収先 収運・処分事業者種別 */
        supplier_trader_type: "Trader",
        /* 回収先 収運・処分事業場ID */
        supplier_trader_location_id: null,
        /* 回収先 収運・処分事業場コード */
        supplier_trader_location_code: null,

        /* 収運業者 */
        transporter_id: null,

        /* 処分事業者ID */
        trader_id: null,
        /* 処分事業場ID */
        trader_location_id: null,
        /* 処分事業場コード */
        trader_location_code: null,

        /* 受注番号ありのみ検索 */
        ordered: false,
        /* 受注番号 */
        order_no: null,
        /* 引取搬入番号 */
        request_no: null,
        /* DXEマニ番号(1次) */
        manifest_id: null,
        /* DXEマニ番号(2次) */
        relation_manifest_id: null,
        /* マニ取消済のみ検索 */
        canceled: false,

        /* 運搬報告状況 */
        transport_report_state: null,
        /* 運搬終了日 開始日 */
        transport_completed_date_from: null,
        /* 運搬終了日 終了日 */
        transport_completed_date_to: null,
        /* 運搬報告区間指定 */
        section_no: null,

        /* 処分報告状況 */
        disposal_report_state: null,
        /* 処分終了日 開始日 */
        disposal_completed_date_from: null,
        /* 処分終了日 終了日 */
        disposal_completed_date_to: null,
        /* 処分方法 */
        disposal_method_ids: null,
        /* 登録業者/JWNET取込業者 */
        self_manifest: null,
        /* 廃棄物の名称 */
        waste_name: null,

        /* 最終報告状況 */
        final_report_state: null,
        /* 最終終了日 開始日 */
        final_disposal_end_from: null,
        /* 最終終了日 終了日 */
        final_disposal_end_to: null,
        /* 排出者承認 */
        approval_state: null,
        /* 表示順 */
        sort_type: "updated_date_desc",

        /* ステータス(EDIのみ) */
        manifest_condition_status: null,
        /* 期限切れ(EDI) */
        expired_status: null,
        /* 期限切れ間近(EDI) */
        neared_status: null,
        /* マニフェスト検索キーワード */
        manifest_search_keyword: null,
        /* 排出事業場コード */
        location_code: null,
        /* 排出事業場名称 */
        location_name: null,

        /* 区間1運搬事業者 */
        section_1_transporter: null,
        /* ルート */
        transport_route_id: null,

        /* カスタムラベル1 */
        custom_labels_1: null,
        /* ラベル①の利用有無 */
        custom_labels_1_unregistered: false,

        /* カスタムラベル2 */
        custom_labels_2: null,
        /* ラベル②の利用有無 */
        custom_labels_2_unregistered: false,

        /* カスタムラベル3 */
        custom_labels_3: null,
        /* ラベル③の利用有無 */
        custom_labels_3_unregistered: false,
        /* カスタムラベル4 */
        custom_labels_4: null,
        /* ラベル④の利用有無 */
        custom_labels_4_unregistered: false,
        /* カスタムラベル5 */
        custom_labels_5: null,
        /* ラベル⑤の利用有無 */
        custom_labels_5_unregistered: false,
        /* カスタムラベル6 */
        custom_labels_6: null,
        /* ラベル⑥の利用有無 */
        custom_labels_6_unregistered: false,

        recovery_order_id: null,
        recovery_order_type: null,
        /* 選択された日付 */
        target_date: null,
        /* レスポンス形式 */
        format: 'json'

      },
      tableHeaders: [
        {
          value: "condition_status",
          text: "ステータス\r\nマニフェスト番号",
          width: "10%",
        },
        {
          value: "order_no",
          text: "DXEマニ番号\r\n受注番号",
          align: "center",
          width: "10%",
        },
        {
          value: "classification_type",
          text: "状態\r\n引渡し日\r\n運搬担当者",
          align: "center",
          width: "10%",
        },
        { value: "emission", text: "回収先\r\n排出事業場", width: "15%" },
        {
          value: "approval_status",
          text: "承認\r\n状況",
          align: "center",
          width: "5%",
        },
        {
          value: "disposer_location",
          text: "収集運搬業者(区間１)\r\n処分事業場",
          width: "15%",
        },
        {
          value: "item_name",
          text: "JWNET取扱品目\r\n処分方法\r\n数量",
          width: "10%",
        },
        {
          value: "created_unix_time",
          text: "登録日\r\n本登録日",
          align: "center",
          width: "5%",
        },
        {
          value: "transport_end_report_date",
          text: "運搬報告(最終区間)\r\n期限通知",
          align: "center",
          width: "10%",
        },
        {
          value: "disposal_end_middle_time",
          text: "中間処分\r\n最終処分",
          align: "center",
          width: "5%",
        },
        { value: "editable", text: "詳細", align: "end", width: "5%" },
      ],
      /**
       * @param {Object[]} manifests - マニフェスト
       * @param {string} manifests[].collected_date - 引渡し日
       * @param {Object} manifests[].driver - ドライバー
       * @param {Object} manifests[].emission - 回収先
       * @param {string} manifests[].emission.name - 回収先事業場名
       * @param {string} manifests[].emission.address - 回収先事業場住所
       *
       * @param {Object} manifests[].emission_location - 排出事業場
       * @param {string} manifests[].emission_location.name - 排出事業場名
       * @param {string} manifests[].emission_location.address - 排出事業場住所
       *
       * @param {Object} manifests[].trader - 運搬先
       * @param {string} manifests[].trader.location_name - 運搬先事業場名
       * @param {string} manifests[].trader.location_address - 運搬先事業場住所
       * @param {number} manifests[].transport_end_report_processed_time - 登録完了日
       * @param {string} manifests[].report_transport_quantity_unit_name - 確定数量単位名
       * @param {string} manifests[].disposal_end_middle_time - 中間処分
       * @param {string} manifests[].final_disposal - 最終処分
       */

      manifests: [],
      targetManifestId: null,
      targetManifestIds: [],
      enableWarningDialog: false,
      enableWarningDialog2: false,
      warningDialogMessage: null,
      warningDialogMessage2: null,
      rowSelected: [],
      invalidManifestNumbers: [],
      invalid_messages: [],
      voteStaffs: [],
      items: [],
      quantityUnits: [],
      valuablesQuantityUnits: [],
      quantityConfirms: [],
      packings: [],
      hazardousSubstances: [],
      transportations: [],
      disposalMethods: [],
      users: [],
      drivers: [],
      vehicles: [],
      bulkRegistErrors: [],
      locations: [],
      traderLocations: [],
      customLabel: null,
      blinkingButton: false,
      stopSearch: false,
    };
  },
  computed: {
    ...mapState("DriverManifestManagement", {
      pagination: "pagination",
      searchCondition: "condition",
      selectableTab: "selectableTab"
    }),
    ...mapState("DriverTenantConfiguration", {
      isDemo: "use_demo",
      isJwMode: "use_jw_mode",
      isEcContractSinglePlan: "use_ec_contract_single_plan",
      isProxyEntry: "use_proxy_entry",
      isRecycling: "use_recycling",
      mode: "mode",
    }),
    ...mapGetters("DriverAuthentication", ["isDemoEnv"]),
    enableCallButton() {
      return !(this.manifests.length > 0 && this.rowSelected.length > 0);
    },
    /**
     * 1週間分の日付を生成
     */
    weekDates() {
      const dates = [];
      const startDate = this.weekStartDate
        ? this.$dayjs(this.weekStartDate)
        : this.$dayjs().startOf("week");

      for (let i = 0; i < 7; i++) {
        const date = startDate.clone().add(i, "days");
        const dayOfWeek = date.day(); // 0:日曜, 1:月曜, ..., 6:土曜
        dates.push({
          date: date.format("YYYY-MM-DD"),
          displayDate: date.format("M/D"),
          dayOfWeek: date.format("ddd"),
          dayNumber: dayOfWeek,
          isToday: date.isSame(this.$dayjs(), "day"),
          isSaturday: dayOfWeek === 6,
          isSunday: dayOfWeek === 0,
        });
      }
      return dates;
    },
  },
  watch: {
    weekStartDate() {
      // 週を移動した際に、選択状態を保持するため、強制的に再設定
      this.$nextTick(() => {
        // searchForm.targetDateは既に保持されているので、何もしない
        // Vue の reactivity により自動的に v-model が再評価される
      });
    },
  },
  created() {
    // 週の開始日を初期化（今週の開始日）
    this.weekStartDate = this.$dayjs().startOf("week").format("YYYY-MM-DD");

    if (this.$route.params.resetCondition) {
      this.resetState();
    }
    if (this.$route.params.resetPagination) {
      this.resetPagination();
    }

    if (this.$route.query.d !== undefined) {
      /*
       * ダッシュボードから
       */
      this.resetState();
      this.conditions.manifest_type = this.$route.params.manifest_type;
      this.conditions.transport_report_state = this.$route.params.transport_report_state;
      this.conditions.end_date = this.$route.params.end_date;
      this.conditions.section_no = this.$route.params.section_no;
      this.conditions.disposal_report_state = this.$route.params.disposal_report_state;
      this.conditions.final_report_state = this.$route.params.final_report_state;
      this.conditions.neared_status = this.$route.params.neared_status;
      this.conditions.expired_status = this.$route.params.expired_status;
    } else if (this.$route.params.order_id) {
      this.resetState();
      this.conditions.recovery_order_id = this.$route.params.order_id;
    } else if (this.$route.params.order_no) {
      this.resetState();
      this.conditions.order_no = this.$route.params.order_no;
    } else {
      Object.assign(this.conditions, this.searchCondition);
    }

    this.selectableConditionPattern = this.selectableTab;

    this.$store.watch(
      (state) => state.DriverManifestManagement.selectableTab,
      (newValue) => {
        this.selectableConditionPattern = newValue;
      }
    );

    this.page = this.pagination.current_page;
    this.perPage = this.searchCondition.limit;

    // 週の開始日を初期化（選択された日付がある場合はその週、なければ今週）
    if (this.conditions.target_date) {
      this.weekStartDate = this.$dayjs(this.conditions.target_date)
        .startOf("week")
        .format("YYYY-MM-DD");
    } else {
      this.weekStartDate = this.$dayjs().startOf("week").format("YYYY-MM-DD");
    }

    // JWモードでかつ新規の場合初期検索しない
    // console.log(`Search: mode: ${this.isJwMode}, resetCondition: ${this.$route.params.resetCondition} search?: ${!(this.isJwMode && this.$route.params.resetCondition)}`);

    /*
     * JWモードで左メニューから表示した場合は検索しない
     */
    if (!(this.isJwMode && this.$route.params.resetCondition)) {
      this.searchManifests();
    }

    this.fetchVoteStaffs().then((content) => {
      this.voteStaffs = content.vote_staffs;
    });

    this.fetchItems().then((content) => {
      this.items = content.items;
    });

    this.loadConditionPatterns()

    this.setMasters();
  },
  methods: {
    ...mapActions("DriverManifestReportConfirmationVerification", {
      verification: "show",
    }),
    ...mapActions("DriverManifestConfirmationSlipVerification", {
      slipVerification: "show",
    }),
    ...mapActions("DriverManifestConfirmationListVerification", {
      listVerification: "show",
    }),
    ...mapActions("DriverManifestManagement", {
      fetchManifests: "index",
      resetState: "resetState",
    }),
    ...mapActions("DriverManifestManagementOutputReservation", {
      outputReserve: "create",
    }),
    ...mapActions("DriverManifestBulkRegistManifest", {
      bulkRegister: "create",
    }),
    ...mapActions("DriverMasterSearchItem", {
      fetchItems: "fetchWholeItems",
    }),
    ...mapActions("DriverMasterSearchQuantityUnit", {
      fetchQuantityUnits: "index",
    }),
    ...mapActions("DriverMasterSearchQuantityConfirm", {
      fetchQuantityConfirms: "index",
    }),
    ...mapActions("DriverMasterSearchPacking", {
      fetchPackings: "index",
    }),
    ...mapActions("DriverMasterSearchHazardousSubstance", {
      fetchHazardousSubstances: "index",
    }),
    ...mapActions("DriverMasterSearchTransportation", {
      fetchTransportations: "index",
    }),
    ...mapActions("DriverMasterSearchDisposalMethod", {
      fetchDisposalMethods: "index",
    }),
    ...mapActions("DriverMasterSearchUser", {
      fetchUsers: "index",
    }),
    ...mapActions("DriverMasterSearchDriver", {
      fetchDrivers: "only_text",
    }),
    ...mapActions("DriverMasterSearchVehicle", {
      fetchVehicles: "search",
    }),
    ...mapActions("DriverMasterSearchCustomLabel", {
      fetchCustomLabel: "index",
    }),
    ...mapActions("DriverMasterSearchVoteStaff", {
      fetchVoteStaffs: "index",
    }),
    ...mapActions("DriverMasterConditionPattern", {
      fetchConditionPatterns: "index",
      fetchConditionPattern: "show",
      destroyConditionPattern: "destroy",
    }),

    ...mapActions("DriverFinalDisposalManifestTypeCheck", {
      finalDisposalTypeCheck: "create",
    }),

    /**
     * 検索パターンタブチェンジ コールバック
     * @param item
     */
    selectableConditionPatternChangeCallback(item) {
      this.stopSearch = true;
      // this.$refs.searchBlock.clearAllSelectableMembers();
      if (item === undefined || item === 0) {
        this.$nextTick(() => {
          Object.assign(this.$data.conditions, this.$options.data().conditions);
          this.searchButtonClickEvent()
        })
      } else if (item > 0) {
        // Object.assign(this.$data.conditions, this.$options.data().conditions);
        const pattern = this.conditionPatterns[item - 1];
        this.fetchConditionPattern({ id: pattern.id }).then((content) => {
          this.$nextTick(()=> {
            this.conditions = content.conditions
            this.searchButtonClickEvent()
          })
        })
      }

    },

    handleUserTabChange () {},

    isDisposalMode() {
      return this.mode === "transport_disposal" || this.mode === "disposal";
    },
    canShowRecyclingDivider() {
      return !this.isRecycling && this.isDisposalMode();
    },
    canShowDefaultDisposalActions() {
      return this.canShowRecyclingDivider() || this.mode === "transport";
    },

    addConditionPatternCallback (item) {
      this.conditionPatterns.push(item);
      this.selectableConditionPattern = this.conditionPatterns.length;
      this.selectableConditionPatternChangeCallback(this.selectableConditionPattern);
    },

    removeConditionPatternCallback (value) {
      const pattern = this.conditionPatterns[value - 1]
      if (pattern) {
        this.destroyConditionPattern({id: pattern.id}).then(() => {
          // タブを移動
          this.selectableConditionPattern = 0
          this.selectableConditionPatternChangeCallback(0);
          this.loadConditionPatterns();
        })
      }
    },
    searchCallback() {
      this.page = 1;
      this.conditions.recovery_order_id = null;
      this.conditions.recovery_order_type = null;
      this.searchManifests();
    },
    clearCallback() {
      this.selectableConditionPatternChangeCallback(this.selectableConditionPattern)
    },
    toggleRowSelection(item) {
      const index = this.rowSelected.findIndex((row) => row === item);
      if (index > -1) {
        // 既に選択されている場合は削除
        this.rowSelected.splice(index, 1);
      } else {
        // 選択されていない場合は追加
        this.rowSelected.push(item);
      }
    },

    /**
     * マスター情報をセットする
     * @return {Promise<void>}
     */
    async setMasters() {
      this.fetchQuantityUnits().then((content) => {
        this.quantityUnits = content.quantity_units;
        this.valuablesQuantityUnits = content.quantity_units.filter(
          (v) => v.code !== "5"
        );
      });

      this.fetchQuantityConfirms().then((content) => {
        this.quantityConfirms = content.quantity_confirms;
      });

      this.fetchPackings().then((content) => {
        this.packings = content.packings;
      });

      this.fetchHazardousSubstances().then((content) => {
        this.hazardousSubstances = content.hazardous_substances;
      });

      this.fetchTransportations().then((content) => {
        this.transportations = content.transportations;
      });

      this.fetchDisposalMethods().then((content) => {
        this.disposalMethods = content.disposal_methods;
      });

      this.fetchUsers({without_invisible: true}).then((content) => {
        this.users = content.users;
      });

      this.fetchDrivers({without_invisible: true}).then((content) => {
        this.drivers = content;
      });

      this.fetchVehicles({ registered_number: "" }).then((content) => {
        this.vehicles = content.vehicles;
      });

      this.fetchCustomLabel().then((content) => {
        this.customLabel = content;
      });
    },

    /**
     * 複写して作成ボタン クリックイベント
     */
    showDuplicateDialogButtonClickEvent() {
      this.enableDuplicateDialog = true;
    },
    /**
     * 複写して作成ダイアログ 複写して作成ボタンコールバック
     * @param manifest_id
     */
    duplicateDialogRegisterCallback(manifest_id) {
      this.$router.push({
        name: "DriverManifestManagementDuplicate",
        params: { duplicate_id: manifest_id },
        query: { plan_date: this.conditions.target_date },
      });
    },
    /**
     * 複写ボタン クリックイベント
     * @param item
     */
    duplicateButtonClickEvent(item) {
      this.$router.push({
        name: "DriverManifestManagementDuplicate",
        params: { duplicate_id: item.id },
        query: { plan_date: this.conditions.target_date },
      });
    },
    /**
     * 編集ボタン クリックイベント
     */
    editButtonClickEvent(item) {
      if (item.modify_type === "editing") {
        this.$router.push({
          name: "DriverManifestManagementEdit",
          params: { id: item.id },
        });
      } else {
        this.targetManifestId = item.id;
        if (["other_form", "paper_form"].includes(item.manifest_type)) {
          this.enableRestEditDialog = true;
        } else {
          this.enableEditDialog = true;
        }
      }
    },
    backToOrderButtonClickEvent() {
      const types = this.conditions.recovery_order_type.match(/(.*)_(.*)/);
      if (types[1] === "pickup") {
        if (types[2] === "r") {
          this.$router.push({
            name: "DriverOrderAcceptPickupRegistrationEdit",
            params: { id: this.conditions.recovery_order_id },
          });
        } else if (types[2] === "d") {
          this.$router.push({
            name: "DriverOrderAcceptPickup",
            params: { id: this.conditions.recovery_order_id },
          });
        }
      } else if (types[1] === "carry_in") {
        if (types[2] === "r") {
          this.$router.push({
            name: "DriverOrderAcceptCarryInRegistrationEdit",
            params: { id: this.conditions.recovery_order_id },
          });
        } else if (types[2] === "d") {
          this.$router.push({
            name: "DriverOrderAcceptCarryIn",
            params: { id: this.conditions.recovery_order_id },
          });
        }
      }
    },
    /**
     * グレードアップ誘導メッセージ表示
     */
    ecContractSinglePlanButtonClickEvent() {
      this.enableEcContractSinglePlanGuideDialog = true;
    },
    /**
     * ダイアログ 閉じるボタンコールバック
     */
    dialogCloseCallback() {
      this.enableEditDialog = false;
      this.enableRestEditDialog = false;
      this.enableDuplicateDialog = false;
      this.enableCompletionDialog = false;
      this.enableBulkCompletionDialog = false;
      this.enableDisposalReportDialog = false;
      this.enableBulkDisposalReportDialog = false;
      this.enableFinalDisposalReportDialog = false;
      this.enableRecycleFinalDisposalReportDialog = false;
      this.enableBulkFinalDisposalReportDialog = false;
      this.enablePaperRegisterDialog = false;
      this.enableBulkPaperRegisterDialog = false;
      this.enableBulkRegistErrorDialog = false;
      this.enableEcContractSinglePlanGuideDialog = false;
      this.enableBulkRecycleFinalDisposalReportDialog = false;
      this.showRecyclingFields = false;
    },
    editDialogRegisterCallback() {
      this.dialogCloseCallback();
      this.searchManifests();
    },
    /**
     * 検索ボタン クリックイベント
     */
    searchButtonClickEvent() {
      this.page = 1;
      this.conditions.recovery_order_id = null;
      this.conditions.recovery_order_type = null;
      this.searchManifests();
    },
    /**
     * １次・２次マニフェストトグル チェンジイベント
     */
    typeOfManifestChangeEvent(value) {
      this.conditions.type_of_manifest = value;
      if (
        this.conditions.type_of_manifest === this.searchCondition.type_of_manifest
      ) {
        return;
      }
      this.manifests = [];
      this.searchButtonClickEvent();
    },
    /**
     * ページ チェンジイベント
     */
    changePage(page) {
      this.page = page;
      this.searchManifests();
    },
    changePerPage(perPage) {
      this.perPage = perPage;
      this.page = 1;
      this.searchManifests();
    },
    searchManifests() {
      this.rowSelected = null;
      this.rowSelected = [];
      this.fetchManifests({ conditions: this.conditions, selectable_tab: this.selectableConditionPattern, page: this.page, limit: this.perPage, format: "json" }).then((content) => {
        this.manifests = content.manifests;
        this.stopSearch = false;
      });
    },
    /**
     * 新規登録ボタン クリックイベント
     */
    addButtonClickEvent() {
      const params = { level: this.conditions.type_of_manifest };
      this.$router.push({
        name: "DriverManifestManagementNew",
        params: params,
        query: { plan_date: this.conditions.target_date },
      });
    },
    /**
     * 詳細ボタン クリックイベント
     */
    showButtonClickEvent(item) {
      let params = { id: item.id };
      if (this.conditions.recovery_order_type) {
        params["order_id"] = this.conditions.recovery_order_id;
        params["order_type"] = this.conditions.recovery_order_type;
      }
      this.$router.push({ name: "DriverManifestManagement", params: params });
    },
    /**
     * 紙マニ返却日(個別)ボタン クリックイベント
     */
    paperManifestDialogShowClickEvent() {
      this.targetManifestIds = [];
      for (const argument of this.rowSelected) {
        this.targetManifestIds.push(argument.id);
      }
      const params = {
        ids: this.targetManifestIds.join(","),
        report_type: "paper_register",
      };
      this.warningDialogMessage = null;
      this.invalidManifestNumbers = [];
      this.verification({ params: params }).then((content) => {
        if (content.status === "success") {
          this.enablePaperRegisterDialog = true;
        } else {
          this.enableWarningDialog = true;
        }
      });
    },
    /**
     * 紙マニ返却日(一括)ボタン クリックイベント
     */
    bulkPaperManifestDialogShowClickEvent() {
      this.targetManifestIds = [];
      for (const argument of this.rowSelected) {
        this.targetManifestIds.push(argument.id);
      }
      const params = {
        ids: this.targetManifestIds.join(","),
        report_type: "paper_register",
      };
      this.warningDialogMessage = null;
      this.invalidManifestNumbers = [];
      this.verification({ params: params }).then((content) => {
        if (content.status === "success") {
          this.enableBulkPaperRegisterDialog = true;
        } else {
          this.enableWarningDialog = true;
        }
      });
    },

    /**
     * 運搬終了報告(個別)ボタン クリックイベント
     * 運搬終了報告が可能であるかをチェックし、可能な場合はモーダルを表示する.
     */
    completionReportDialogClickEvent() {
      this.targetManifestIds = [];
      for (const argument of this.rowSelected) {
        this.targetManifestIds.push(argument.id);
      }
      const params = {
        ids: this.targetManifestIds.join(","),
        report_type: "completion",
      };
      this.warningDialogMessage = null;
      // this.invalidManifestNumbers = []
      this.verification({ params: params }).then((content) => {
        if (content.status === "success") {
          this.enableCompletionDialog = true;
        } else {
          // this.invalidManifestNumbers = content.invalid_manifest_numbers
          this.error_message = content.error_message;
          this.invalid_messages = content.invalid_messages;
          this.enableWarningDialog = true;
        }
      });
    },

    /**
     * 運搬終了報告(一括)ボタン クリックイベント
     */
    bulkCompletionReportDialogClickEvent() {
      this.targetManifestIds = [];
      for (const argument of this.rowSelected) {
        this.targetManifestIds.push(argument.id);
      }
      const params = {
        ids: this.targetManifestIds.join(","),
        report_type: "completion",
        is_bulk: true,
      };
      this.warningDialogMessage = null;
      // this.invalidManifestNumbers = []
      this.verification({ params: params }).then((content) => {
        if (content.status === "success") {
          this.enableBulkCompletionDialog = true;
        } else {
          // this.invalidManifestNumbers = content.invalid_manifest_numbers
          this.invalid_messages = content.invalid_messages;
          this.enableWarningDialog = true;
        }
      });
    },

    /**
     * 処分終了報告(個別)ボタン クリックイベント
     * @param showRecyclingFields { Boolean }
     */
    disposalReportDialogClickEvent(showRecyclingFields = false) {
      this.showRecyclingFields = showRecyclingFields;
      this.targetManifestIds = [];
      for (const argument of this.rowSelected) {
        this.targetManifestIds.push(argument.id);
      }
      const params = {
        ids: this.targetManifestIds.join(","),
        report_type: "disposal",
      };
      this.warningDialogMessage = null;
      // this.invalidManifestNumbers = []
      this.verification({ params: params }).then((content) => {
        if (content.status === "success") {
          this.enableDisposalReportDialog = true;
        } else {
          this.invalid_messages = content.invalid_messages;
          this.enableWarningDialog = true;
        }
      });
    },

    /**
     * 処分終了報告(一括)ボタン クリックイベント
     * @param showRecyclingFields { Boolean }
     */
    bulkDisposalReportDialogClickEvent(showRecyclingFields = false) {
      this.showRecyclingFields = showRecyclingFields;
      this.targetManifestIds = [];
      for (const argument of this.rowSelected) {
        this.targetManifestIds.push(argument.id);
      }
      const params = {
        ids: this.targetManifestIds.join(","),
        report_type: "disposal",
        is_bulk: true,
      };
      this.warningDialogMessage = null;
      // this.invalidManifestNumbers = []
      this.verification({ params: params }).then((content) => {
        if (content.status === "success") {
          this.enableBulkDisposalReportDialog = true;
        } else {
          // this.invalidManifestNumbers = content.invalid_manifest_numbers
          this.invalid_messages = content.invalid_messages;
          this.enableWarningDialog = true;
        }
      });
    },

    /**
     * 最終処分終了報告(個別)ボタン クリックイベント
     * @param type
     */
    finalDisposalReportDialogClickEvent(type = 'default') {
      this.targetManifestIds = [];
      for (const argument of this.rowSelected) {
        this.targetManifestIds.push(argument.id);
      }
      const params = {
        ids: this.targetManifestIds.join(","),
        report_type: "final_disposal",
        is_recycle: type === 'recycle',
      };
      this.warningDialogMessage = null;
      // this.invalidManifestNumbers = []
      this.verification({ params: params }).then((content) => {
        if (content.status === "success") {

          if (type === 'default') {
            this.finalDisposalManifestTypeCheck('single')
            // this.enableFinalDisposalReportDialog = true;
          } else {
            this.enableRecycleFinalDisposalReportDialog = true;
          }

        } else {
          this.invalid_messages = content.invalid_messages;
          this.enableWarningDialog = true;
        }
      });
    },

    /**
     * 最終処分終了報告(一括)ボタン クリックイベント
     * @param type
     */
    bulkFinalDisposalReportDialogClickEvent(type = 'default') {
      this.targetManifestIds = [];
      for (const argument of this.rowSelected) {
        this.targetManifestIds.push(argument.id);
      }
      const params = {
        ids: this.targetManifestIds.join(","),
        report_type: "final_disposal",
        is_bulk: true,
        is_recycle: type === 'recycle',
      };
      this.warningDialogMessage = null;
      // this.invalidManifestNumbers = []
      this.verification({ params: params }).then((content) => {
        if (content.status === "success") {
          if (type === 'default') {
            this.finalDisposalManifestTypeCheck('collective')
          } else {
            /*
             * 選択されたマニフェストの処分場がすべて一致しているかのチェック処理
             * 複数で別々の処分場が選択されてしまうと、異なる処理場で登録された処理フローが選択・登録できてしまうので
             * 必ず同一の処理場のマニフェストが選択されていることを条件に一括モーダルを表示する。
             * 混在している場合はエラーとし、処理を続行させない。
             */
            const selectedManifests = this.manifests.filter(v => this.targetManifestIds.includes(v.id));
            const disposerKeys = new Set(
              selectedManifests.map(manifest => manifest.disposer_key)
            );

            const firstLevelMethodDetailKeys = new Set(selectedManifests.map(manifest => manifest.first_level_method_detail_ids))

            const hasDifferDisposer = disposerKeys.size > 1;
            const hasDifferMethodDetailKey = firstLevelMethodDetailKeys.size > 1;
            if (hasDifferDisposer || hasDifferMethodDetailKey) {
              this.warningDialogMessage2 = "同時に登録できないマニフェストが選択されています。"
              this.enableWarningDialog2 = true;
            } else {
              this.enableBulkRecycleFinalDisposalReportDialog = true;
            }
          }
        } else {
          this.invalid_messages = content.invalid_messages;
          this.enableWarningDialog = true;
        }
      });
    },

    /**
     * 本登録ボタン クリックイベント
     */
    registerManifestButtonClickEvent() {
      this.targetManifestIds = [];
      for (const argument of this.rowSelected) {
        this.targetManifestIds.push(argument.id);
      }
      let modalOption = {
        title: "一括本登録",
        message: "本登録します。よろしいですか？",
        options: { titleColor: "blue" },
      };
      this.$refs.confirm.open(modalOption).then((confirm) => {
        this.$refs.confirm.close();
        if (confirm) {
          let params = { manifest_ids: this.targetManifestIds };
          this.bulkRegister({ params: params }).then((content) => {
            if (content.has_error) {
              this.bulkRegistErrors = content.errors;
              this.enableBulkRegistErrorDialog = true;
            } else {
              this.bulkRegistErrors = [];
              this.searchManifests();
            }
          });
        }
      });
    },

    /**
     * 受渡確認票（自社区間）出力ボタン クリックイベント
     * @description 受渡確認票を別タブにて表示する
     */
    outputManifestButtonClickEvent() {
      let selectedIds = [];
      for (const argument of this.rowSelected) {
        selectedIds.push(argument.id);
      }
      const params = {
        mode: "current",
        targets: selectedIds,
        sort_type: this.conditions.sort_type,
      };
      this.slipVerification({ params: params }).then((content) => {
        if (content.status === "success") {
          let resolvedRoute = this.$router.resolve({
            name: "DriverManifestConfirmationSlips",
            query: {
              mode: "current",
              targets: selectedIds,
              sort_type: this.conditions.sort_type,
            },
          });
          window.open(resolvedRoute.href, "_blank");
        } else {
          this.warningDialogMessage2 = content.error_message;
          this.enableWarningDialog2 = true;
        }
      });
    },

    /**
     * 受渡確認票（全区間）出力ボタン クリックイベント
     * @description 受渡確認票を別タブにて表示する
     */
    outputAllManifestButtonClickEvent() {
      let selectedIds = [];
      for (const argument of this.rowSelected) {
        selectedIds.push(argument.id);
      }
      const params = {
        mode: "all",
        targets: selectedIds,
        sort_type: this.conditions.sort_type,
      };
      this.slipVerification({ params: params }).then((content) => {
        if (content.status === "success") {
          let resolvedRoute = this.$router.resolve({
            name: "DriverManifestConfirmationSlips",
            query: {
              mode: "all",
              targets: selectedIds,
              sort_type: this.conditions.sort_type,
            },
          });
          window.open(resolvedRoute.href, "_blank");
        } else {
          this.warningDialogMessage2 = content.error_message;
          this.enableWarningDialog2 = true;
        }
      });
    },

    /**
     * 一覧表（全区間）出力ボタン クリックイベント
     * @description 一覧表を別タブにて表示する
     */
    outputAllListButtonClickEvent() {
      let selectedIds = [];
      for (const argument of this.rowSelected) {
        selectedIds.push(argument.id);
      }
      const params = {
        mode: "all",
        targets: selectedIds,
        sort_type: this.conditions.sort_type,
      };
      this.listVerification({ params: params }).then((content) => {
        if (content.status === "success") {
          let resolvedRoute = this.$router.resolve({
            name: "DriverManifestConfirmationLists",
            query: {
              mode: "all",
              targets: selectedIds,
              sort_type: this.conditions.sort_type,
            },
          });
          window.open(resolvedRoute.href, "_blank");
        } else {
          this.warningDialogMessage2 = content.error_message;
          this.enableWarningDialog2 = true;
        }
      });
    },

    /**
     * CSV出力(全件出力)ボタン クリックイベント
     */
    exportButtonClickEvent() {
      const parameters = this.buildOutputReserveParams();
      this.outputReserve({ conditions: parameters, layout: "all" }).then(() => {
        this.blinkingButton = true;
      });
    },

    /**
     * CSV出力(直行用出力)ボタン クリックイベント
     */
    exportDirectButtonClickEvent() {
      const parameters = this.buildOutputReserveParams();
      this.outputReserve({ conditions: parameters, layout: "direct" }).then(
        () => {
          this.blinkingButton = true;
        }
      );
    },

    /**
     * CSV出力(積替保管用出力)ボタン クリックイベント
     */
    exportTransshipmentButtonClickEvent() {
      const parameters = this.buildOutputReserveParams();
      this.outputReserve({
        conditions: parameters,
        layout: "transshipment",
      }).then(() => {
        this.blinkingButton = true;
      });
    },

    /**
     * CSV出力(マニフェスト取り込み用出力)ボタン クリックイベント
     */
    exportImportSampleButtonClickEvent() {
      const parameters = this.buildOutputReserveParams();
      this.outputReserve({ conditions: parameters, layout: "import" }).then(
        () => {
          this.blinkingButton = true;
        }
      );
    },

    /**
     * CSV取込ボタン クリックイベント
     */
    importManifestButtonClickEvent() {
      /** 電子契約単体プランの場合、メッセージを出して終わる */
      if (this.isEcContractSinglePlan) {
        this.enableEcContractSinglePlanGuideDialog = true;
      } else {
        this.$router.push({ name: "DriverManifestManagementCsvImport" });
      }
    },

    /**
     * 受注番号 クリックイベント
     */
    orderNoClickEvent(item) {
      const params = { order_no: item.order_no };
      this.$router.push({ name: "DriverOrderAccepts", params: params });
    },

    /**
     * CSV出力用パラメータ作成
     */
    buildOutputReserveParams() {
      const outputParams = this.conditions;
      delete outputParams.recovery_order_id
      delete outputParams.recovery_order_type;
      delete outputParams.target_date;
      return outputParams;
    },

    /**
     * 前の週に移動
     */
    moveToPreviousWeek() {
      this.weekStartDate = this.$dayjs(this.weekStartDate)
        .subtract(7, "days")
        .format("YYYY-MM-DD");
    },

    /**
     * 次の週に移動
     */
    moveToNextWeek() {
      this.weekStartDate = this.$dayjs(this.weekStartDate)
        .add(7, "days")
        .format("YYYY-MM-DD");
    },

    /**
     * 日付ボタンクリック
     * @param date - クリックされた日付（YYYY-MM-DD形式）
     */
    dateButtonClick(date) {
      // 既に選択されている日付をクリックした場合は選択解除
      if (this.conditions.target_date === date) {
        this.conditions.target_date = null;
        this.conditions.start_date = null;
        this.conditions.end_date = null;
      } else {
        // 選択された日付を設定
        this.conditions.target_date = date;
        this.conditions.start_date = date;
        this.conditions.end_date = date;
      }

      // 検索を実行
      this.searchButtonClickEvent();
    },

    /**
     * 検索パターン読込
     */
    loadConditionPatterns() {
      /**
       * @param content { Object }
       * @property content.condition_patterns { Array }
       */
      this.fetchConditionPatterns({ classification: "manifests" }).then(
        (content) => {
          this.conditionPatterns = content.condition_patterns;
        }
      );
    },

    /**
     * マニフェスト一覧の各行に複写メニューを表示するかの判定
     * @param item
     * @return {null|string|*|boolean}
     */
    canShowDuplicateManu (item) {
      /*
       * ＜１次マニ＋他社区間代行起票OFF＞
       *  自社起票で区間１が他社収運の「EDI」のマニフェストには複写メニューを表示しない。「WEB・紙」の場合は表示する。
       * 先日の打合せ内容通りで現状ステージングにも上記仕様でアップ済。
       * ＜２次マニ＋他社区間代行起票OFF＞
       * １次マニと異なり、２次マニでは複写メニューは、自社起票のマニであればすべて複写メニューを表示する。
       * 「複写して作成」ダイアログ画面でも同様の仕様とする。
       */
      if (this.conditions.type_of_manifest === 'first_manifest') {
        return item.self_manifest && (this.isProxyEntry || !item.proxy_request || item.manifest_type !== 'dxe_form');
      } else {
        return item.self_manifest;
      }
    },

    /**
     * 最終処分終了報告(紙形式)押下時のチェック処理
     * @param modalType { String } single, collective
     */
    finalDisposalManifestTypeCheck (modalType) {
      const params = { manifest_ids: this.targetManifestIds }
      this.finalDisposalTypeCheck({ params: params }).then((response) => {
        if (modalType === 'single') {
          this.enableFinalDisposalReportDialog = true;
        } else {
          this.enableBulkFinalDisposalReportDialog = true;
        }
      });
    },

  },
};
</script>

<style scoped lang="scss">
.panel-header-color {
  color: #0d74ba !important;
  & .v-expansion-panel-header__icon {
    & .v-icon {
      color: #0d74ba !important;
      &::before {
        color: #0d74ba !important;
      }
    }
  }
}
.panel-selectable {
  background-color: #dce7eb;
}
.week-selector {
  display: inline-flex;
  gap: 4px;
}
.date-button {
  width: 48px !important;
  height: 48px !important;
  min-width: 48px !important;
  border-radius: 0 !important;
  font-size: 0.75rem !important;
  padding: 0 !important;
}
.date-button.today-border {
  border: 1px solid #1976d2 !important;
  box-shadow: 0 0 4px rgba(25, 118, 210, 0.5) !important;
}
.text-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 0 1 auto;
  min-width: 0;
}
.chip-ellipsis {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.inline-flex-container {
  display: inline-flex;
  align-items: center;
  gap: 0;
  max-width: 100%;
}
.ellipsis-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.map-icon {
  flex-shrink: 0;
}
.line-clamp {
  width: 100%;
}
</style>
